import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Bug, Lightbulb, Sparkles, Paperclip, CheckCircle2, Clock, Mail, Send, Loader2, MessageSquare, X } from "lucide-react";
import { useState, useRef } from "react";
import { AdminComposeEmailDialog, type EmailRecipient } from "@/components/admin-compose-email-dialog";

interface AdminFeedback {
  id: string;
  userId: string;
  type: string;
  page: string | null;
  message: string;
  attachmentUrl: string | null;
  status: string;
  adminReply: string | null;
  repliedAt: string | null;
  replyEmailSentAt: string | null;
  replyEmailOpenedAt: string | null;
  createdAt: string | null;
  userEmail: string | null;
  userFirstName: string | null;
  userLastName: string | null;
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function TypeBadge({ type }: { type: string }) {
  if (type === "bug") return <Badge variant="destructive" className="gap-1"><Bug className="h-3 w-3" />Bug Report</Badge>;
  if (type === "idea") return <Badge variant="secondary" className="gap-1"><Lightbulb className="h-3 w-3" />Idea</Badge>;
  if (type === "contact") return <Badge className="gap-1 bg-violet-500 hover:bg-violet-500"><Mail className="h-3 w-3" />Contact</Badge>;
  return <Badge variant="outline" className="gap-1"><Sparkles className="h-3 w-3" />Improvement</Badge>;
}

function StatusBadge({ status }: { status: string }) {
  if (status === "new") return <Badge className="gap-1 bg-blue-500 hover:bg-blue-500"><Mail className="h-3 w-3" />New</Badge>;
  if (status === "read") return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" />Read</Badge>;
  return <Badge variant="outline" className="gap-1 text-green-600 border-green-200"><CheckCircle2 className="h-3 w-3" />Resolved</Badge>;
}

type TabKey = "feedback" | "contact" | "sent" | "replies";
const MAX_REPLY_ATTACHMENTS = 10;

interface AdminMailMessage {
  id: string;
  userId: string;
  recipientEmail: string;
  recipientName: string | null;
  fromEmail?: string | null;
  product: "builder" | "engineer";
  direction: "sent" | "received";
  subject: string;
  body: string;
  threadId?: string | null;
  createdAt: string;
}

interface AdminMailResponse {
  sent: AdminMailMessage[];
  replies: AdminMailMessage[];
  syncError?: string;
}

function FeedbackCard({ item, onStatusChange, onReply }: {
  item: AdminFeedback;
  onStatusChange: (id: string, status: string) => void;
  onReply: (id: string, reply: string, files?: File[]) => Promise<boolean>;
}) {
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const userName = [item.userFirstName, item.userLastName].filter(Boolean).join(" ") || item.userEmail || "Unknown user";

  const handleSend = async () => {
    if (!replyText.trim()) return;
    setIsSending(true);
    const emailSent = await onReply(item.id, replyText, attachments);
    setIsSending(false);
    setReplyText("");
    setAttachments([]);
    setShowReply(false);
    toast({
      title: emailSent ? "Reply sent by email" : "Reply saved",
      description: emailSent
        ? `Email accepted for sending to ${item.userEmail}${attachments.length ? ` with ${attachments.length} attachment${attachments.length === 1 ? "" : "s"}` : ""}`
        : item.userEmail
          ? "Could not send email — check SMTP settings"
          : "User has no email address on file",
    });
  };

  return (
    <Card
      className={item.status === "new" ? "border-blue-200 dark:border-blue-800" : ""}
      data-testid={`card-feedback-${item.id}`}
    >
      <CardContent className="pt-4 pb-4 space-y-3">
        {/* Top row */}
        <div className="flex flex-col sm:flex-row sm:items-start gap-3">
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <TypeBadge type={item.type} />
              <StatusBadge status={item.status} />
              <span className="text-xs text-muted-foreground ml-auto">{formatDate(item.createdAt)}</span>
            </div>
            <p className="text-sm font-medium" data-testid={`text-feedback-user-${item.id}`}>
              {userName}
              {item.userEmail && item.userFirstName && (
                <span className="font-normal text-muted-foreground ml-1">({item.userEmail})</span>
              )}
            </p>
            {item.page && <p className="text-xs text-muted-foreground">Page: {item.page}</p>}
            <p className="text-sm whitespace-pre-wrap" data-testid={`text-feedback-message-${item.id}`}>{item.message}</p>
            {item.attachmentUrl && (
              <a
                href={`/api/uploads/view?path=${encodeURIComponent(item.attachmentUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary underline"
                data-testid={`link-attachment-${item.id}`}
              >
                <Paperclip className="h-3 w-3" />View attachment
              </a>
            )}
          </div>
          <div className="flex-shrink-0">
            <Select
              value={item.status}
              onValueChange={(val) => onStatusChange(item.id, val)}
              data-testid={`select-status-${item.id}`}
            >
              <SelectTrigger className="h-8 w-32 text-xs" data-testid={`trigger-status-${item.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="read">Read</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Existing reply */}
        {item.adminReply && (
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <p className="text-xs font-medium text-primary">Your reply · {formatDate(item.repliedAt)}</p>
              {item.replyEmailOpenedAt ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Opened · {formatDate(item.replyEmailOpenedAt)}
                </span>
              ) : item.replyEmailSentAt ? (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Send className="h-3.5 w-3.5" />
                  Sent · {formatDate(item.replyEmailSentAt)}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">Saved internally · not emailed</span>
              )}
            </div>
            <p className="text-sm whitespace-pre-wrap">{item.adminReply}</p>
          </div>
        )}

        {/* Reply section */}
        {showReply ? (
          <div className="space-y-2">
            <Textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={`Reply to ${item.userEmail || "this user"}...`}
              className="resize-y text-sm min-h-[80px]"
              rows={5}
              data-testid={`input-reply-${item.id}`}
            />
            {/* Attachment pills */}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid={`reply-attachments-${item.id}`}>
                {attachments.map((file, index) => (
                  <div key={`${file.name}-${file.size}-${file.lastModified}-${index}`} className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted rounded px-2.5 py-1.5 max-w-full">
                    <Paperclip className="h-3 w-3 shrink-0" />
                    <span className="truncate max-w-[240px]">{file.name}</span>
                    <span className="text-muted-foreground/60 shrink-0">({(file.size / 1024).toFixed(0)} KB)</span>
                    <button
                      type="button"
                      onClick={() => setAttachments(current => current.filter((_, fileIndex) => fileIndex !== index))}
                      className="ml-0.5 shrink-0 hover:text-destructive transition-colors"
                      aria-label={`Remove ${file.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf,.doc,.docx,.txt"
              multiple
              className="hidden"
              data-testid={`input-file-${item.id}`}
              onChange={(e) => {
                const selectedFiles = Array.from(e.target.files ?? []);
                if (selectedFiles.length > 0) {
                  setAttachments(current => {
                    const combined = [...current, ...selectedFiles];
                    const unique = combined.filter((file, index, files) =>
                      files.findIndex(candidate =>
                        candidate.name === file.name &&
                        candidate.size === file.size &&
                        candidate.lastModified === file.lastModified,
                      ) === index,
                    );
                    return unique.slice(0, MAX_REPLY_ATTACHMENTS);
                  });
                }
                // Allow choosing the same file again after removing it.
                e.currentTarget.value = "";
              }}
            />
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                onClick={handleSend}
                disabled={!replyText.trim() || isSending}
                data-testid={`button-send-reply-${item.id}`}
              >
                {isSending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                {item.userEmail ? "Send Email" : "Save Reply"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid={`button-attach-${item.id}`}
              >
                <Paperclip className="h-3.5 w-3.5 mr-1.5" />
                {attachments.length ? `Add files (${attachments.length}/${MAX_REPLY_ATTACHMENTS})` : "Attach files"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowReply(false); setReplyText(""); setAttachments([]); if (fileInputRef.current) fileInputRef.current.value = ""; }}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowReply(true)}
            data-testid={`button-reply-${item.id}`}
          >
            <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
            {item.adminReply ? "Edit Reply" : "Reply"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminInboxPage() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<TabKey>("feedback");
  const [mailProduct, setMailProduct] = useState<"builder" | "engineer">("builder");
  const [replyTo, setReplyTo] = useState<AdminMailMessage | null>(null);

  const { data: feedbackList, isLoading } = useQuery<AdminFeedback[]>({
    queryKey: ["/api/admin/feedback"],
    refetchInterval: 30_000,
  });

  const isMailTab = activeTab === "sent" || activeTab === "replies";
  const mailQuery = useQuery<AdminMailResponse>({
    queryKey: ["/api/admin/mail", mailProduct],
    queryFn: async () => {
      const response = await fetch(`/api/admin/mail?product=${mailProduct}`, { credentials: "include" });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(data?.message || "Could not load messages");
      return data;
    },
    enabled: isMailTab,
    staleTime: 0,
    refetchInterval: isMailTab ? 60_000 : false,
    refetchOnWindowFocus: true,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiRequest("PATCH", `/api/admin/feedback/${id}/status`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/feedback"] }),
    onError: () => toast({ title: "Failed to update status", variant: "destructive" }),
  });

  const replyMutation = useMutation({
    mutationFn: ({ id, reply, files }: { id: string; reply: string; files?: File[] }) => {
      const form = new FormData();
      form.append("reply", reply);
      for (const file of files ?? []) form.append("attachments", file);
      return fetch(`/api/admin/feedback/${id}/reply`, {
        method: "POST",
        body: form,
        credentials: "include",
      }).then(r => r.json());
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/feedback"] }),
  });

  const handleReply = async (id: string, reply: string, files?: File[]): Promise<boolean> => {
    const result = await replyMutation.mutateAsync({ id, reply, files });
    return result?.emailSent === true;
  };

  const feedbackItems = feedbackList?.filter(f => f.type !== "contact") ?? [];
  const contactItems = feedbackList?.filter(f => f.type === "contact") ?? [];
  const displayed = activeTab === "feedback" ? feedbackItems : activeTab === "contact" ? contactItems : [];

  const newFeedbackCount = feedbackItems.filter(f => f.status === "new").length;
  const newContactCount = contactItems.filter(f => f.status === "new").length;

  const tabs: { key: TabKey; label: string; count: number; newCount: number }[] = [
    { key: "feedback", label: "Feedback", count: feedbackItems.length, newCount: newFeedbackCount },
    { key: "contact", label: "Contact", count: contactItems.length, newCount: newContactCount },
    { key: "sent", label: "Sent", count: mailQuery.data?.sent.length ?? 0, newCount: 0 },
    { key: "replies", label: "Replies", count: mailQuery.data?.replies.length ?? 0, newCount: 0 },
  ];

  if (isLoading) {
    return (
      <div className="p-6 space-y-4 max-w-5xl mx-auto">
        <Skeleton className="h-8 w-48" />
        <div className="flex gap-2"><Skeleton className="h-9 w-28" /><Skeleton className="h-9 w-28" /></div>
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5 max-w-5xl mx-auto">
      <h1 className="text-lg sm:text-2xl font-bold" data-testid="text-admin-inbox-title">Inbox</h1>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 -mb-px ${
              activeTab === tab.key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`tab-${tab.key}`}
          >
            {tab.label}
            <span className={`text-xs rounded-full px-1.5 py-0.5 ${
              activeTab === tab.key ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
            }`}>
              {tab.count}
            </span>
            {tab.newCount > 0 && (
              <span className="text-xs rounded-full px-1.5 py-0.5 bg-blue-500 text-white">
                {tab.newCount} new
              </span>
            )}
          </button>
        ))}
      </div>

      {isMailTab ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-1 rounded-lg border p-1" aria-label="Email product">
              {(["builder", "engineer"] as const).map(value => (
                <Button key={value} size="sm" variant={mailProduct === value ? "secondary" : "ghost"} onClick={() => setMailProduct(value)}>
                  {value === "builder" ? "Builders" : "Engineers"}
                </Button>
              ))}
            </div>
            <Button size="sm" variant="outline" disabled={mailQuery.isFetching} onClick={() => void mailQuery.refetch()}>
              {mailQuery.isFetching ? "Reloading…" : "Reload messages"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Customer replies are checked about once a minute while this page is open.</p>
          {mailQuery.isError && (
            <p role="alert" className="text-sm text-destructive">{mailQuery.error instanceof Error ? mailQuery.error.message : "Could not load messages"}</p>
          )}
          {mailQuery.data?.syncError && (
            <p role="alert" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              Gmail replies could not be refreshed: {mailQuery.data.syncError}. Sent messages remain visible.
            </p>
          )}
          {mailQuery.isLoading ? (
            <div className="space-y-3">{[1, 2].map(n => <Skeleton key={n} className="h-32 w-full" />)}</div>
          ) : (activeTab === "sent" ? mailQuery.data?.sent : mailQuery.data?.replies)?.length ? (
            <div className="space-y-3">
              {(activeTab === "sent" ? mailQuery.data?.sent : mailQuery.data?.replies)?.map(message => (
                <Card key={message.id} data-testid={`card-admin-mail-${message.id}`}>
                  <CardContent className="py-4 space-y-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium">{message.subject}</p>
                        <p className="text-xs text-muted-foreground">
                          {message.direction === "sent" ? "To" : "From"}: {message.direction === "received" ? message.fromEmail || message.recipientEmail : message.recipientEmail}
                          {message.recipientName ? ` · ${message.recipientName}` : ""}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground">{formatDate(message.createdAt)}</span>
                    </div>
                    <p className="whitespace-pre-wrap break-words text-sm">{message.body}</p>
                    {message.userId && message.recipientEmail && (
                      <Button size="sm" variant="outline" onClick={() => setReplyTo(message)} data-testid={`button-reply-mail-${message.id}`}>
                        <Mail className="mr-1.5 h-3.5 w-3.5" /> {message.threadId ? "Reply by email" : "New email"}
                      </Button>
                    )}
                    {!message.threadId && <p className="text-xs text-muted-foreground">Older feedback emails were sent before Gmail conversation tracking. Replies to them will not appear here.</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : !mailQuery.isError ? (
            <Card><CardContent className="py-16 text-center text-muted-foreground">
              {activeTab === "sent" ? "No emails sent to users yet." : "No customer replies yet."}
            </CardContent></Card>
          ) : null}
        </div>
      ) : displayed.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            No {activeTab === "feedback" ? "feedback" : "contact messages"} yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {displayed.map(item => (
            <FeedbackCard
              key={item.id}
              item={item}
              onStatusChange={(id, status) => updateStatusMutation.mutate({ id, status })}
              onReply={handleReply}
            />
          ))}
        </div>
      )}
      <AdminComposeEmailDialog
        recipient={replyTo ? {
          id: replyTo.userId,
          email: replyTo.recipientEmail,
          name: replyTo.recipientName || replyTo.recipientEmail,
          product: replyTo.product,
        } satisfies EmailRecipient : null}
        replyToMessageId={replyTo?.threadId ? replyTo.id : undefined}
        initialSubject={replyTo ? (replyTo.subject.startsWith("Re:") ? replyTo.subject : `Re: ${replyTo.subject}`) : ""}
        onClose={() => setReplyTo(null)}
      />
    </div>
  );
}
